import { useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { UploadSimple, X, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ImageDropzone({ images, onChange }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    const uploadFiles = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of files) {
                if (!f.type.startsWith("image/")) continue;
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push({
                    id: data.id,
                    path: data.path,
                    is_main: false,
                    order: (images?.length || 0) + uploaded.length,
                });
            }
            const next = [...(images || []), ...uploaded];
            if (!next.some((i) => i.is_main) && next.length) next[0].is_main = true;
            onChange(next);
        } catch (e) {
            const detail = e?.response?.data?.detail || "Failed to upload image";
            toast.error(detail);
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        uploadFiles(Array.from(e.dataTransfer.files));
    };

    const removeAt = (idx) => {
        const next = images.filter((_, i) => i !== idx);
        if (!next.some((i) => i.is_main) && next.length) next[0].is_main = true;
        onChange(next);
    };

    const setMain = (idx) => {
        onChange(images.map((img, i) => ({ ...img, is_main: i === idx })));
    };

    const moveUp = (idx) => {
        if (idx === 0) return;
        const next = [...images];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        onChange(next.map((im, i) => ({ ...im, order: i })));
    };

    return (
        <div>
            <div
                data-testid="image-dropzone"
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
                    dragging ? "border-sage-dark bg-[#F2F2F2]" : "border-[#DDDDDD] bg-[#F9F9F9] hover:bg-[#F2F2F2]"
                }`}
            >
                <UploadSimple size={28} weight="light" className="mx-auto mb-2 text-neutral-500" />
                <p className="text-sm text-neutral-600">
                    {uploading ? "Uploading…" : "Drag & drop images here, or click to select"}
                </p>
                <p className="text-[11px] text-neutral-400 mt-1">PNG, JPG, WEBP up to ~10MB</p>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    data-testid="image-dropzone-input"
                    onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                />
            </div>

            {images?.length > 0 && (
                <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3">
                    {images.map((img, idx) => (
                        <div key={img.id || idx} className="relative group border border-mist rounded-sm overflow-hidden">
                            <div className="aspect-square bg-[#F6F5F2]">
                                <img src={fileUrl(img.path)} alt="" className="w-full h-full object-cover" />
                            </div>
                            {img.is_main && (
                                <span className="absolute top-1.5 left-1.5 bg-sage-dark text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full">
                                    Main
                                </span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 bg-white/95 flex items-center justify-around py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    type="button"
                                    onClick={() => setMain(idx)}
                                    title="Set as main"
                                    className="text-neutral-700 hover:text-sage-dark"
                                    data-testid={`set-main-${idx}`}
                                >
                                    <Star size={14} weight={img.is_main ? "fill" : "light"} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveUp(idx)}
                                    title="Move up"
                                    className="text-neutral-700 hover:text-sage-dark text-xs"
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeAt(idx)}
                                    title="Remove"
                                    className="text-neutral-700 hover:text-red-600"
                                    data-testid={`remove-image-${idx}`}
                                >
                                    <X size={14} weight="light" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
